import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaHistory, FaBullseye, FaHandshake, FaGlobe, FaUserTie } from 'react-icons/fa';
import OurBranches from '../components/common/OurBranches';

const About = () => {
    const { t } = useTranslation();


    return (
        <div className="min-h-screen bg-white text-gray-800 font-sans">
            {/* 1. Hero Section - Corporate Style */}
            <section className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-4 block">{t('about.badge')}</span>
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t('about.title')}</h1>
                    <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                        {t('about.subtitle')}
                    </p>
                </div>
            </section>

            {/* 2. History & Introduction */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row gap-12 items-center">
                        <div className="md:w-1/2">
                            <div className="bg-primary/5 p-8 rounded-2xl border-l-4 border-primary">
                                <FaHistory className="text-4xl text-primary mb-6" />
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('about.history.title')}</h2>
                                <div className="space-y-4 text-gray-700 leading-relaxed">
                                    <p>{t('about.history.establishment')}</p>
                                    <p>{t('about.history.location')}</p>
                                    <p>{t('about.history.milestones')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2 grid grid-cols-2 gap-6">
                            {/* Stats or Visuals could go here, simplified for now */}
                            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-secondary text-center">
                                <h3 className="text-4xl font-bold text-primary mb-2">10+</h3>
                                <p className="text-gray-500 text-sm uppercase tracking-wide">{t('about.stats.yearsOfTrust')}</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-secondary text-center">
                                <h3 className="text-4xl font-bold text-primary mb-2">10K+</h3>
                                <p className="text-gray-500 text-sm uppercase tracking-wide">{t('about.stats.happyMembers')}</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-secondary text-center">
                                <h3 className="text-4xl font-bold text-primary mb-2">3</h3>
                                <p className="text-gray-500 text-sm uppercase tracking-wide">{t('about.stats.branches')}</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-lg border-t-4 border-secondary text-center">
                                <h3 className="text-4xl font-bold text-primary mb-2">24/7</h3>
                                <p className="text-gray-500 text-sm uppercase tracking-wide">{t('about.stats.support')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. Vision & Mission */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-secondary font-bold tracking-widest uppercase text-xs mb-3 block">{t('about.visionBadge')}</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4">{t('about.visionMissionHeading')}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Vision */}
                        <div className="bg-white rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group">
                            <div className="bg-primary p-6 flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-white">{t('visionMissionPage.vision.title')}</h3>
                                <FaGlobe className="text-white/80 text-3xl" />
                            </div>
                            <div className="p-8">
                                <p className="text-lg text-gray-600 leading-relaxed italic">
                                    "{t('visionMissionPage.vision.content')}"
                                </p>
                            </div>
                        </div>

                        {/* Mission */}
                        <div className="bg-white rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group">
                            <div className="bg-primary p-6 flex items-center justify-between">
                                <h3 className="text-2xl font-bold text-white">{t('visionMissionPage.mission.title')}</h3>
                                <FaBullseye className="text-white/80 text-3xl" />
                            </div>
                            <div className="p-8">
                                <p className="text-lg text-gray-600 leading-relaxed italic">
                                    "{t('visionMissionPage.mission.content')}"
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Core Values Integrated */}
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg border border-gray-100 shadow-sm hover:shadow-md text-center">
                            <div className="w-14 h-14 bg-blue-50 text-primary rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                                <FaHandshake />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('visionMissionPage.values.trust')}</h4>
                            <p className="text-gray-500">{t('visionMissionPage.values.trustDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg border border-gray-100 shadow-sm hover:shadow-md text-center">
                            <div className="w-14 h-14 bg-blue-50 text-primary rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                                <FaGlobe />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('visionMissionPage.values.transparency')}</h4>
                            <p className="text-gray-500">{t('visionMissionPage.values.transparencyDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg border border-gray-100 shadow-sm hover:shadow-md text-center">
                            <div className="w-14 h-14 bg-blue-50 text-primary rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                                <FaUserTie />
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('visionMissionPage.values.empowerment')}</h4>
                            <p className="text-gray-500">{t('visionMissionPage.values.empowermentDesc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. Leadership & Management */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-secondary font-bold tracking-widest uppercase text-xs mb-3 block">{t('about.leadershipBadge')}</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4">{t('management.title')}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-20">
                        {/* Founder */}
                        <div className="bg-white rounded-xl shadow-lg border-l-4 border-secondary overflow-hidden flex flex-col md:flex-row items-center">
                            <div className="md:w-1/3 p-6 flex justify-center">
                                <img src="/kaka.png" alt={t('about.founder.name')} className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-xl shadow-md border-2 border-secondary/20" />
                            </div>
                            <div className="p-8 flex-1">
                                <span className="text-secondary font-bold uppercase text-xs tracking-wider mb-2 block">{t('about.founderBadge')}</span>
                                <h3 className="text-2xl font-bold text-primary mb-4">{t('about.founder.name')}</h3>
                                <p className="text-gray-600 italic">"{t('about.founder.message')}"</p>
                            </div>
                        </div>

                        {/* Chairman */}
                        <div className="bg-white rounded-xl shadow-lg border-l-4 border-secondary overflow-hidden flex flex-col md:flex-row items-center">
                            <div className="md:w-1/3 p-6 flex justify-center">
                                <img src="/chandrakant.png" alt={t('about.chairman.name')} className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-xl shadow-md border-2 border-secondary/20" />
                            </div>
                            <div className="p-8 flex-1">
                                <span className="text-secondary font-bold uppercase text-xs tracking-wider mb-2 block">{t('about.chairmanBadge')}</span>
                                <h3 className="text-2xl font-bold text-primary mb-4">{t('about.chairman.name')}</h3>
                                <p className="text-gray-600 italic">"{t('about.chairman.message')}"</p>
                            </div>
                        </div>
                    </div>

                    {/* Board Members Grid */}
                    <div className="bg-gray-50 rounded-2xl p-10">
                        <h3 className="text-2xl font-bold text-center text-primary-dark mb-10">{t('management.board.title')}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {/* Static Placeholders mimicking current structure */}
                            <div className="text-center group">
                                <div className="w-24 h-24 bg-white border-2 border-secondary rounded-full mx-auto mb-4 overflow-hidden shadow-md group-hover:scale-110 transition-transform">
                                    <img src="/kaka.png" alt={t('management.board.chairmanName')} className="w-full h-full object-cover" />
                                </div>
                                <h4 className="font-bold text-gray-900">{t('management.board.chairman')}</h4>
                                <p className="text-sm text-gray-500">{t('management.board.chairmanName')}</p>
                            </div>
                            <div className="text-center group">
                                <div className="w-24 h-24 bg-white border-2 border-secondary rounded-full mx-auto mb-4 overflow-hidden shadow-md group-hover:scale-110 transition-transform">
                                    <img src="/chandrakant.png" alt={t('management.board.viceChairmanName')} className="w-full h-full object-cover" />
                                </div>
                                <h4 className="font-bold text-gray-900">{t('management.board.viceChairman')}</h4>
                                <p className="text-sm text-gray-500">{t('management.board.viceChairmanName')}</p>
                            </div>
                            <div className="text-center group">
                                <div className="w-24 h-24 bg-white border-2 border-secondary rounded-full mx-auto mb-4 overflow-hidden shadow-md group-hover:scale-110 transition-transform duration-300">
                                    <img
                                        src="/Vishal.png"
                                        alt={t('management.board.secretaryName')}
                                        className="w-full h-full object-cover object-top"
                                        onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Vishal+Mhetre&background=003366&color=fff' }}
                                    />
                                </div>
                                <h4 className="font-bold text-gray-900">{t('management.board.secretary')}</h4>
                                <p className="text-sm text-gray-500">{t('management.board.secretaryName')}</p>
                            </div>

                            {/* Dynamic List of Directors and Advisors */}
                            {(t('management.board.list', { returnObjects: true }) || []).map((member, idx) => (
                                <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
                                    <h4 className="font-bold text-primary mb-1">{member.role}</h4>
                                    <p className="text-gray-700 font-medium">{member.name}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. Branches & Network */}
            <OurBranches />
        </div>
    );
};

export default About;
